import { Edge, Node } from '@xyflow/react';

import { DEFAULT_NODE_HEIGHT, DEFAULT_NODE_WIDTH } from './constants';
import { graphlib, layout } from '@dagrejs/dagre';

const dagreGraph = new graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

// Define a generic base node interface
export interface HierarchyNodeBase {
  id?: string | null;
  parentId?: string | null;
  level?: number | null;
}

export function buildHierarchyTree<T extends HierarchyNodeBase>(
  nodes: T[]
): (T & { children: T[]; level: number })[] {
  const nodeMap = new Map<string, T & { children: T[]; level: number }>();
  const roots: (T & { children: T[]; level: number })[] = [];

  for (const node of nodes) {
    if (node.id != null) {
      nodeMap.set(node.id, {
        ...node,
        children: [],
        level: node.level ?? 0,
      });
    }
  }

  nodeMap.forEach((node) => {
    const parent = node.parentId ? nodeMap.get(node.parentId) : null;
    if (parent) {
      parent.children.push(node);
      node.level = (parent.level ?? 0) + 1;
    } else {
      roots.push(node);
    }
  });

  return roots;
}

// Create layouted elements for React Flow using dagre

const createDagreGraph = () => {
  const g = new graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  return g;
};

export type LayoutDirection = 'TB' | 'BT' | 'LR' | 'RL';

interface LayoutOptions {
  direction?: LayoutDirection;
  defaultNodeWidth?: number;
  defaultNodeHeight?: number;
}

/**
 * Applies Dagre layout to nodes and edges with adaptive spacing.
 */
type NodeWithLevel<T extends Record<string, unknown>> = Node<T> & {
  level?: number;
};

export function getLayoutedElements<T extends Record<string, unknown>>(
  nodes: Node<T>[],
  edges: Edge[],
  options: LayoutOptions = {}
): { nodes: Node<T>[]; edges: Edge[] } {
  const {
    direction = 'TB',
    defaultNodeWidth = DEFAULT_NODE_WIDTH,
    defaultNodeHeight = DEFAULT_NODE_HEIGHT,
  } = options;

  const graph = createDagreGraph();

  // Count incoming edges
  const incoming = new Map<string, number>();
  for (const edge of edges) {
    incoming.set(edge.target, (incoming.get(edge.target) || 0) + 1);
  }

  // Track nodes with their levels
  const nodeMap = new Map<string, NodeWithLevel<T>>();
  for (const node of nodes) {
    nodeMap.set(node.id, { ...node });
  }

  const queue: Array<{ id: string; level: number }> = [];
  for (const node of nodes) {
    if (!incoming.has(node.id)) {
      queue.push({ id: node.id, level: 0 });
    }
  }

  const levelGroups = new Map<number, NodeWithLevel<T>[]>();

  while (queue.length > 0) {
    const { id, level } = queue.shift()!;
    const node = nodeMap.get(id);
    if (!node || node.level !== undefined) continue;

    node.level = level;
    if (!levelGroups.has(level)) levelGroups.set(level, []);
    levelGroups.get(level)!.push(node);

    for (const edge of edges) {
      if (edge.source === id) {
        queue.push({ id: edge.target, level: level + 1 });
      }
    }
  }

  // Add nodes to Dagre
  for (const node of Array.from(nodeMap.values())) {
    graph.setNode(node.id, {
      width: defaultNodeWidth,
      height: defaultNodeHeight,
    });
  }

  // Add edges to Dagre

  for (const edge of Array.from(edges.values())) {
    graph.setEdge(edge.source, edge.target);
  }

  // Configure and perform Dagre layout
  graph.setGraph({
    rankdir: direction,
    ranksep: 0,
    nodesep: 20,
    edgesep: 0,
    marginx: 50,
    marginy: 0,
  });

  layout(graph);
  
  // Custom Y-offsets by level
  const levelOffsets = new Map<number, number>();
  let accumulatedY = 0;

  const sortedLevels = Array.from(levelGroups.keys()).sort((a, b) => a - b);
  for (const level of sortedLevels) {
    const group = levelGroups.get(level)!;
    const nextGroup = levelGroups.get(level + 1);
    const allTree = group.every((n) => (n.data as any)?.isTree === true);
    const isChildSingleNode = nextGroup?.length === 1 && level !== 0;

    const ranksep =
      allTree || isChildSingleNode
        ? defaultNodeHeight
        : level === 0
        ? defaultNodeHeight + 100
        : defaultNodeHeight * 2;

    levelOffsets.set(level, accumulatedY);

    accumulatedY += ranksep;
  }

  const layoutedNodes = nodes.map((node) => {
    const dagreNode = graph.node(node.id);
    const level = nodeMap.get(node.id)?.level ?? 0;
    const offsetY = levelOffsets.get(level) ?? 0;

    return {
      ...node,
      position: {
        x: dagreNode.x - defaultNodeWidth / 2,
        y: offsetY,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}
